import React, {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { OAuth2QueryParameters } from "@features/authentication/constants/Oauth2";
import OAuth2Service, {
  Client,
} from "@features/authentication/services/OAuth2Service";
import { useLocation } from "react-router-dom";
import { SuccessTokenStepResponse, ApiError } from "@shared/services";
import { StartAuthenticationRequest } from "@features/authentication/services/OAuth2Request";
import { useRecaptchaContext } from "./RecaptchaContext";
import { OpenIdService } from "@shared/services";

export interface OAuth2Type {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  code_challenge?: string;
  code_challenge_method?: string;
}

export interface OAuth2ContextType {
  oauth2Data: OAuth2Type;
  setOauth2Data: Dispatch<SetStateAction<OAuth2Type>>;
  client: Client | null;
  state: string;
  isResolvingSession: boolean;
  startAuthenticationRequestResponse: SuccessTokenStepResponse | ApiError | undefined;
  isRecaptchaReady: boolean;
  recaptchaError: string | null;
  isTimeout?: boolean;
}

const OAuth2Context = createContext<OAuth2ContextType | undefined>(undefined);

type OAuth2ProviderProps = { children: ReactNode };
const RECAPTCHA_ERROR_TEXT = "reCAPTCHA Error";

function buildRecaptchaApiError(message: string): ApiError {
  return {
    name: "ApiError",
    message,
    url: "",
    status: 0,
    statusText: RECAPTCHA_ERROR_TEXT,
    body: { error: message, errorCode: "RECAPTCHA_ERROR" },
    request: {} as any,
  };
}

export function scheduleNextFrame(callback: () => void): number {
  if (typeof requestAnimationFrame !== 'undefined') {
    return requestAnimationFrame(callback);
  }
  return setTimeout(callback, 16) as any;
}

export function cancelNextFrame(id: number): void {
  if (typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(id);
  } else {
    clearTimeout(id as any);
  }
}

function cleanupOngoingRequest(
  isMounted: boolean,
  ongoingRequestRef: React.MutableRefObject<Promise<any> | null>,
  timeoutIdRef: React.MutableRefObject<NodeJS.Timeout | null>,
): void {
  if (!isMounted) return;
  ongoingRequestRef.current = null;
  clearTimeoutRef(timeoutIdRef);
}

function clearTimeoutRef(timeoutIdRef: React.MutableRefObject<NodeJS.Timeout | null>): void {
  if (timeoutIdRef.current) {
    clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = null;
  }
}

export function extractRecaptchaValues(ctx: any) {
  if (!ctx) {
    return {
      antiBotToken: null as string | null,
      executeRecaptcha: undefined as any,
      isRecaptchaReadyCheck: undefined as (() => boolean) | undefined,
      recaptchaCurrentToken: undefined as string | undefined,
      recaptchaContextError: undefined as string | undefined,
    };
  }
  return {
    antiBotToken: ctx.currentToken as string | null,
    executeRecaptcha: ctx.executeRecaptcha,
    isRecaptchaReadyCheck: ctx.isReady as (() => boolean) | undefined,
    recaptchaCurrentToken: ctx.currentToken as string | undefined,
    recaptchaContextError: ctx.error as string | undefined,
  };
}

export function getTokenToUse(recaptchaCurrentToken: string | undefined, antiBotToken: string | null): string {
  if (recaptchaCurrentToken) return recaptchaCurrentToken;
  if (antiBotToken) return antiBotToken;
  throw new Error('Anti-bot token is required but not available. Please ensure reCAPTCHA is executed.');
}

export function isUnauthorizedResponse(response: any): boolean {
  return (response as any)?.status === 401 || (response as any)?.response?.status === 401;
}

export function handleRetryError(retryError: unknown, isMounted: boolean): { errorMessage: string } | null {
  if (!isMounted) return null;
  const errorMessage = retryError instanceof Error ? retryError.message : String(retryError);
  return { errorMessage };
}

export function shouldSkipTimeout(isMounted: boolean, hasResponse: boolean, loadingStartTime: number | null): boolean {
  return !isMounted || hasResponse || loadingStartTime === null;
}

export function hasTimeoutError(isTimeout: boolean, startAuthenticationRequestResponse: SuccessTokenStepResponse | ApiError | undefined): boolean {
  if (!isTimeout || !startAuthenticationRequestResponse) return false;
  const error = startAuthenticationRequestResponse as ApiError;
  return error.statusText === "Timeout" || !!error.message?.includes("timeout");
}

const OAUTH2_PARAMS_KEY = "oauth2_params";

/** Persist OAuth2 params in sessionStorage so they survive page refreshes. */
function persistOAuth2Params(params: OAuth2Type): void {
  if (hasRequiredOAuth2Parameters(params)) {
    try { sessionStorage.setItem(OAUTH2_PARAMS_KEY, JSON.stringify(params)); }
    catch { /* quota exceeded or unavailable -- ignore */ }
  }
}

/** Recover OAuth2 params from sessionStorage after a page refresh. */
export function getPersistedOAuth2Params(): OAuth2Type | null {
  try {
    const stored = sessionStorage.getItem(OAUTH2_PARAMS_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as OAuth2Type;
  } catch { return null; }
}

/**
 * Resolve the onboarding state token for this page.
 *
 * The onboarding redirect carries an opaque `session` handle instead of the
 * token itself, which does not fit the query string size limit enforced at
 * the edge. A token still present inline wins, so a redirect issued by an
 * older backend keeps working.
 */
export function useResolvedState(
  inlineState: string,
  sessionHandle: string
): { state: string; isResolvingSession: boolean } {
  const [resolvedState, setResolvedState] = useState<string>("");
  const [isResolvingSession, setIsResolvingSession] = useState<boolean>(
    () => Boolean(sessionHandle) && !inlineState
  );

  useEffect(() => {
    if (inlineState || !sessionHandle) return undefined;

    let cancelled = false;
    setIsResolvingSession(true);

    OpenIdService.getOnboardingSession(sessionHandle)
      .then((response) => { if (!cancelled) setResolvedState(response.state ?? ""); })
      .catch(() => { if (!cancelled) setResolvedState(""); })
      .finally(() => { if (!cancelled) setIsResolvingSession(false); });

    return () => { cancelled = true; };
  }, [inlineState, sessionHandle]);

  return { state: inlineState || resolvedState, isResolvingSession };
}

export const OAuth2Provider = ({ children }: OAuth2ProviderProps) => {
  const [startAuthenticationRequestResponse, setStartAuthenticationRequestResponse] = useState<SuccessTokenStepResponse | ApiError>()
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  
  // Move useLocation() to component level to follow Rules of Hooks
  const location = useLocation();
  const urlParams: OAuth2Type = ExtractOAuth2QueryParameters(location);
  const oauth2Params: OAuth2Type = hasRequiredOAuth2Parameters(urlParams)
    ? urlParams
    : (getPersistedOAuth2Params() ?? urlParams);
  if (hasRequiredOAuth2Parameters(oauth2Params)) {
    persistOAuth2Params(oauth2Params);
  }
  const [oauth2Data, setOauth2Data] = useState<OAuth2Type>({
    ...oauth2Params,
  });

  // Track ongoing requests to prevent duplicates
  const ongoingRequestRef = useRef<Promise<SuccessTokenStepResponse | ApiError> | null>(null);
  const loadingStartTimeRef = useRef<number | null>(null);
  const hasResponseRef = useRef<boolean>(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const oauth2Service = new OAuth2Service();
  const client = oauth2Service.getClientInformation(oauth2Data);
  const inlineState = ExtractParameter(OAuth2QueryParameters.STATE, location);
  const sessionHandle = ExtractParameter(OAuth2QueryParameters.SESSION, location);
  const { state, isResolvingSession } = useResolvedState(inlineState, sessionHandle);

  // Get antiBotToken, executeRecaptcha, and isReady from RecaptchaContext
  const recaptchaContext = useRecaptchaContext();
  const { antiBotToken, executeRecaptcha, isRecaptchaReadyCheck, recaptchaCurrentToken, recaptchaContextError } = extractRecaptchaValues(recaptchaContext);

  // Track if we've already initialized reCAPTCHA to prevent multiple executions
  const recaptchaInitializedRef = useRef<boolean>(false);
  const lastExecuteRecaptchaRef = useRef<typeof executeRecaptcha | undefined>(undefined);
  const lastIsRecaptchaReadyCheckRef = useRef<typeof isRecaptchaReadyCheck | undefined>(undefined);

  // Synchronously execute reCAPTCHA at the start of the flow
  useEffect(() => {
    const recaptchaRefsChanged = lastExecuteRecaptchaRef.current !== executeRecaptcha || 
        lastIsRecaptchaReadyCheckRef.current !== isRecaptchaReadyCheck;
    if (recaptchaRefsChanged) {
      recaptchaInitializedRef.current = false;
      lastExecuteRecaptchaRef.current = executeRecaptcha;
      lastIsRecaptchaReadyCheckRef.current = isRecaptchaReadyCheck;
    }

    const alreadyInitialized = recaptchaInitializedRef.current || isRecaptchaReady;
    if (alreadyInitialized) {
      return;
    }

    let isMounted = true;
    let animationFrameId: number | null = null;
    const maxWaitTime = 10000;
    const startTime = Date.now();

    const resolveOnce = (resolve: (v: boolean) => void, resolved: { value: boolean }, result: boolean) => {
      if (resolved.value) return;
      resolved.value = true;
      resolve(result);
    };

    const shouldStopPolling = (resolved: { value: boolean }): boolean =>
      resolved.value || !isMounted;

    const isRecaptchaReadyNow = (): boolean =>
      !!(isRecaptchaReadyCheck && isRecaptchaReadyCheck());

    const isMaxWaitExceeded = (): boolean =>
      Date.now() - startTime > maxWaitTime;

    const pollRecaptchaReady = (resolve: (v: boolean) => void, resolved: { value: boolean }) => {
      if (shouldStopPolling(resolved)) { resolveOnce(resolve, resolved, false); return; }
      if (isRecaptchaReadyNow()) { resolveOnce(resolve, resolved, true); return; }
      if (isMaxWaitExceeded()) { resolveOnce(resolve, resolved, false); return; }
      animationFrameId = scheduleNextFrame(() => pollRecaptchaReady(resolve, resolved));
    };

    const waitForRecaptchaReady = (): Promise<boolean> =>
      new Promise((resolve) => pollRecaptchaReady(resolve, { value: false }));

    const handleRecaptchaSuccess = () => {
      if (!isMounted) return;
      recaptchaInitializedRef.current = true;
      setIsRecaptchaReady(true);
      setRecaptchaError(null);
      setIsTimeout(false);
    };

    const handleRecaptchaError = (error: unknown) => {
      if (!isMounted) return;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setRecaptchaError(errorMessage);
      setIsRecaptchaReady(false);
      setIsTimeout(false);
      console.error('[CreateAccountPage] reCAPTCHA initialization failed:', errorMessage);
      setStartAuthenticationRequestResponse(buildRecaptchaApiError(errorMessage));
    };

    const assertRecaptchaAvailable = () => {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA is not available. Cannot proceed without anti-bot token.');
      }
    };

    const assertRecaptchaReady = async () => {
      const isReady = await waitForRecaptchaReady();
      if (!isReady) {
        throw new Error('reCAPTCHA widget did not become ready within the timeout period. Please refresh the page.');
      }
    };

    const assertTokenObtained = (recaptchaResult: { success: boolean; token?: string; error?: string }) => {
      const tokenObtained = recaptchaResult.success && !!recaptchaResult.token;
      if (!tokenObtained) {
        throw new Error(`Failed to get anti-bot token from reCAPTCHA: ${recaptchaResult.error || 'Unknown error'}`);
      }
    };

    const executeRecaptchaToken = async () => {
      assertRecaptchaAvailable();
      await assertRecaptchaReady();
      const recaptchaResult = await executeRecaptcha!();
      assertTokenObtained(recaptchaResult);
    };

    const initializeRecaptcha = async () => {
      try {
        const currentToken = recaptchaContext?.currentToken;
        if (currentToken) {
          handleRecaptchaSuccess();
          return;
        }
        await executeRecaptchaToken();
        handleRecaptchaSuccess();
      } catch (error) {
        handleRecaptchaError(error);
      }
    };

    initializeRecaptcha();

    return () => {
      isMounted = false;
      if (animationFrameId !== null) {
        cancelNextFrame(animationFrameId);
      }
    };
  }, [executeRecaptcha, isRecaptchaReadyCheck, isRecaptchaReady]);

  useEffect(() => {
    let isMounted = true;
    
    const clearStateSafely = () => {
      loadingStartTimeRef.current = null;
      hasResponseRef.current = true;
    };
    
    if (!isRecaptchaReady) {
      clearStateSafely();
      setIsTimeout(false);
      return;
    }

    const currentToken = recaptchaCurrentToken;
    if (!currentToken) return;

    const getTokenToUseLocal = (): string => {
      return getTokenToUse(recaptchaCurrentToken, antiBotToken);
    };

    const handleSuccessfulResponse = (response: SuccessTokenStepResponse | ApiError) => {
      if (!isMounted) return response;
      hasResponseRef.current = true;
      clearTimeoutRef(timeoutIdRef);
      setStartAuthenticationRequestResponse(response);
      return response;
    };

    const handleRetryRecaptchaFailure = (error: string) => {
      if (!isMounted) return;
      setRecaptchaError(error);
      setIsRecaptchaReady(false);
      setStartAuthenticationRequestResponse(buildRecaptchaApiError(error));
    };

    const handleRetrySuccess = async (recaptchaResult: { success: boolean; token?: string; error?: string }): Promise<SuccessTokenStepResponse | ApiError | null> => {
      if (!recaptchaResult.success || !recaptchaResult.token) {
        if (recaptchaResult.error) handleRetryRecaptchaFailure(recaptchaResult.error);
        return null;
      }
      const retryResponse = await StartAuthenticationRequestResponse(oauth2Data, recaptchaResult.token);
      return handleSuccessfulResponse(retryResponse);
    };

    const clearCurrentRecaptchaToken = () => {
      if (recaptchaContext?.setCurrentToken) recaptchaContext.setCurrentToken(null);
    };

    const handleRetryErrorLocal = (retryError: unknown): null => {
      const result = handleRetryError(retryError, isMounted);
      if (result) {
        setRecaptchaError(result.errorMessage);
        setIsRecaptchaReady(false);
      }
      return null;
    };

    const retryWithNewToken = async (): Promise<SuccessTokenStepResponse | ApiError | null> => {
      if (!executeRecaptcha) return null;
      try {
        clearCurrentRecaptchaToken();
        const recaptchaResult = await executeRecaptcha();
        return handleRetrySuccess(recaptchaResult);
      } catch (retryError) {
        return handleRetryErrorLocal(retryError);
      }
    };

    const clearStateIfActive = () => {
      if (!isMounted) return;
      clearStateSafely();
      setIsTimeout(false);
      clearTimeoutRef(timeoutIdRef);
    };

    const handleRequestError = async (error: any, tokenToUse: string): Promise<SuccessTokenStepResponse | ApiError | null> => {
      clearStateIfActive();
      if (isUnauthorizedResponse(error) && tokenToUse) {
        const retryResponse = await retryWithNewToken();
        if (retryResponse) return retryResponse;
      }
      return null;
    };

    const fetchData = async (): Promise<SuccessTokenStepResponse | ApiError> => {
      if (!isRecaptchaReady || !currentToken) {
        throw new Error('[SECURITY] StartAuthenticationRequest called before anti-bot token was resolved. This violates the sequential gate.');
      }
      const tokenToUse = getTokenToUseLocal();
      try {
        const response = await StartAuthenticationRequestResponse(oauth2Data, tokenToUse);
        if (isUnauthorizedResponse(response)) throw response;
        clearStateIfActive();
        return handleSuccessfulResponse(response);
      } catch (error: any) {
        const retryResponse = await handleRequestError(error, tokenToUse);
        if (retryResponse) return retryResponse;
        throw error;
      } finally {
        cleanupOngoingRequest(isMounted, ongoingRequestRef, timeoutIdRef);
      }
    };

    const handleExistingRequest = () => {
      const settle = (error?: any) => {
        if (!isMounted) return;
        clearStateSafely();
        setIsTimeout(false);
        clearTimeoutRef(timeoutIdRef);
        if (error) console.error('OAuth2 initialization failed:', error);
      };
      ongoingRequestRef.current!
        .then((response) => { settle(); setStartAuthenticationRequestResponse(response); })
        .catch((error) => settle(error));
    };

    const shouldSkipTimeoutLocal = (): boolean =>
      shouldSkipTimeout(isMounted, hasResponseRef.current, loadingStartTimeRef.current);

    const startTimeoutTimer = (durationMs: number) => {
      clearTimeoutRef(timeoutIdRef);
      timeoutIdRef.current = setTimeout(() => {
        if (shouldSkipTimeoutLocal()) return;
        const elapsed = Date.now() - loadingStartTimeRef.current!;
        if (elapsed < durationMs) return;
        setIsTimeout(true);
        hasResponseRef.current = true;
        loadingStartTimeRef.current = null;
        setStartAuthenticationRequestResponse({
          name: "ApiError",
          message: "Request timeout - initial loading took too long",
          url: "",
          status: 0,
          statusText: "Timeout",
          body: { error: "timeout" },
          request: {} as any,
        });
      }, durationMs);
    };

    const startNewRequest = () => {
      loadingStartTimeRef.current = Date.now();
      setIsTimeout(false);
      hasResponseRef.current = false;
      startTimeoutTimer(60000);
      ongoingRequestRef.current = fetchData();
    };

    const hasTimeoutErrorLocal = (): boolean => {
      return hasTimeoutError(isTimeout, startAuthenticationRequestResponse);
    };

    const noOAuth2Params = !hasRequiredOAuth2Parameters(oauth2Data);
    const timeoutErrorOccurred = hasTimeoutErrorLocal();
    const hasExistingRequest = !!ongoingRequestRef.current;

    const clearOAuth2State = () => {
      clearStateSafely();
      setIsTimeout(false);
      setStartAuthenticationRequestResponse(undefined);
    };

    const dispatchRequest = () => {
      if (hasExistingRequest) handleExistingRequest();
      else startNewRequest();
    };

    if (noOAuth2Params) clearOAuth2State();
    else if (!timeoutErrorOccurred) dispatchRequest();

    return () => {
      isMounted = false;
      hasResponseRef.current = true;
      clearTimeoutRef(timeoutIdRef);
    };
    // isTimeout and startAuthenticationRequestResponse intentionally excluded to prevent infinite loops
  }, [oauth2Data, isRecaptchaReady, recaptchaCurrentToken, recaptchaContext, executeRecaptcha]);

  useEffect(() => {
    const hasRecaptchaError = !!recaptchaContextError;
    if (hasRecaptchaError) {
      handleRecaptchaContextError(recaptchaContextError!);
    } else {
      clearRecaptchaErrorIfNull();
    }
  }, [recaptchaContextError, startAuthenticationRequestResponse]);

  const handleRecaptchaContextError = (error: string) => {
    setRecaptchaError(error);
    setIsRecaptchaReady(false);
    const noExistingResponse = !startAuthenticationRequestResponse;
    if (noExistingResponse) {
      setStartAuthenticationRequestResponse(buildRecaptchaApiError(error));
    }
  };

  const clearRecaptchaErrorIfNull = () => {
    if (recaptchaContext?.error === null) {
      setRecaptchaError(null);
    }
  };

  const initialValues = useMemo(
    () => ({ oauth2Data, setOauth2Data, client, startAuthenticationRequestResponse, state, isResolvingSession, isRecaptchaReady, recaptchaError, isTimeout }),
    [oauth2Data, setOauth2Data, client, startAuthenticationRequestResponse, state, isResolvingSession, isRecaptchaReady, recaptchaError, isTimeout]
  );

  return (
    <OAuth2Context.Provider value={initialValues}>
      {children}
    </OAuth2Context.Provider>
  );
};

export const useOAuth2 = () => {
  const context = useContext(OAuth2Context);

  if (!context)
    throw new Error("useOAuth2 must be used within an OAuth2Provider");

  return context;
};

function extractOAuth2RequiredParams(queryParams: URLSearchParams) {
  return {
    client_id: queryParams.get(OAuth2QueryParameters.CLIENT_ID) ?? "",
    response_type: queryParams.get(OAuth2QueryParameters.RESPONSE_TYPE) ?? "",
    redirect_uri: queryParams.get(OAuth2QueryParameters.REDIRECT_URI) ?? "",
    scope: queryParams.get(OAuth2QueryParameters.SCOPE) ?? "",
  };
}

export function ExtractOAuth2QueryParameters(location: any): OAuth2Type {
  const queryParams = new URLSearchParams(location.search);
  return {
    ...extractOAuth2RequiredParams(queryParams),
    code_challenge: queryParams.get(OAuth2QueryParameters.CODE_CHALLENGE) ?? undefined,
    code_challenge_method: queryParams.get(OAuth2QueryParameters.CODE_CHALLENGE_METHOD) ?? undefined,
  };
}

function hasRequiredOAuth2Parameters(oauth2QueryParameters: OAuth2Type): boolean {
  const { client_id, response_type, redirect_uri } = oauth2QueryParameters;
  return Boolean(client_id && response_type && redirect_uri);
}

async function StartAuthenticationRequestResponse(oauth2Data: OAuth2Type, antiBotToken: string): Promise<SuccessTokenStepResponse | ApiError> {
  try {
    return await StartAuthenticationRequest(oauth2Data, undefined, antiBotToken);
  } catch (e) {
    return JSON.parse(JSON.stringify(e));
  }
}

export function ExtractParameter(parameter: string, location: any): string {
  const queryParams = new URLSearchParams(location.search);
  return queryParams.get(parameter) || "";
}